/**
 * Screen Router — 6 屏的 hash 路由（v1.1.0：新增屏 0 首页）
 *
 * 屏列表：
 *   #/                       → 屏 0 首页（赛博指挥终端）
 *   #/levels                 → 屏 1 关卡列表
 *   #/projects               → 兼容重定向 → 屏 1 关卡列表
 *   #/editor/:projectId      → 屏 2 Vibe Coding 编辑器（含右侧 vibe-panel 侧栏直发）
 *   #/battle/:projectId      → 屏 4 战斗执行中
 *   #/result/:projectId      → 屏 5 + 5.5 战斗结果
 *   #/settings               → 屏 6 设置
 *   #/sharpen                → 屏 10 砺锋台（V5 唯一入口，V6-010）
 *   #/sharpen-transition     → 屏 11 砺锋台中转页（V8 强制中转，2026-07-20）
 *   #/forge                  → 屏 12 铸剑阁（学习体系入口）
 *   #/my-works               → 屏 13 我的作品集（V9 新增，2026-07-22）
 *
 * 真机反馈 #2（2026-07-05，方案 A）：移除 `editor-vibe` 路由（Vibe 弹窗废弃）。
 * v1.0.9（2026-07-09）：移除 `#/submit/:projectId` 路由（屏 3 移除），老 URL 落屏 1。
 * v1.1.0（2026-07-12）：新增 `#/` → 屏 0 首页、`#/levels` → 关卡列表，`#/projects` 兼容重定向。
 * v6.1.0（2026-07-20）：砺锋台 V5 唯一化 — 移除 `#/climb` / `#/sharpen2` / `#/sharpen3` 三个旧路由。
 *   旧路由经 `DEPRECATED_SHARPEN_HASHES` 自动跳 `#/sharpen` + console.warn（避免书签/外链悄悄失效）。
 * v8.0.0（2026-07-20）：新增 `#/sharpen-transition` → 屏 11 砺锋台中转页（剑从鞘中抽出 + 铸剑行绝句）。
 *   首页/关卡列表的砺锋台入口改为先经过中转页，动画结束自动跳 `#/sharpen`（不可跳过）。
 * v9.0.0（2026-07-22）：新增 `#/my-works` → 屏 13 我的作品集（作品选择器 + 铸剑阁成就展示）。
 */

export type ScreenId =
  | "home"          // v1.1.0 屏 0: 首页（赛博指挥终端）
  | "levels"        // v1.1.0 屏 1: 关卡列表
  | "projects"      // 兼容旧路由 → 重定向到 levels
  | "editor"
  | "battle"
  | "result"
  | "settings"
  | "editor-map"   // V2 屏 7: 地图编辑器（Phase 4 实现）
  | "arena"        // 屏 8: Evolution Arena
  | "showcase-validator"  // 屏 9: Showcase Validator（展示关卡回归验证）
  | "sharpen"      // V6.1 屏 10: 砺锋台 V5 唯一入口（V6-010）
  | "sharpen-transition" // V8 屏 11: 砺锋台中转页（强制中转，2026-07-20）
  | "forge"        // V8 屏 12: 铸剑阁（学习体系入口）
  | "my-works"     // V9 屏 13: 我的作品集（作品选择器 + 铸剑阁成就展示，2026-07-22）
  | "armory-hall"  // V11 屏 14: 兵主殿（导航枢纽，2026-07-24）
  | "transition"   // V11 屏 15: 通用过渡动画（参数化，2026-07-24）
  | "auth-callback"   // V11: 微信登录回调（JWT 解析 + 重定向首页）
  | "snapshot-collector"; // V13 屏 16: Test Harness 快照采集（headless 战斗 + golden 导出）

export interface RouteState {
  screen: ScreenId;
  projectId: string | null;
}

// v6.1.0: 旧砺锋台路由自动跳新路由 + console.warn
const DEPRECATED_SHARPEN_HASHES: Record<string, string> = {
  "#/climb": "V6-008 climb 旧代码已移除，#",
  "#/sharpen2": "V6-009 sharpen-v2 旧代码已移除，#",
  "#/sharpen3": "V6-010 开发期临时路由已合并，#",
};

const ROUTES: Array<{ pattern: RegExp; map: (m: RegExpMatchArray) => RouteState }> = [
  // v1.1.0: #/ 和空 hash → 首页
  { pattern: /^#?\/?$/, map: () => ({ screen: "home", projectId: null }) },
  // v1.1.0: #/levels → 关卡列表
  { pattern: /^#?\/levels$/, map: () => ({ screen: "levels", projectId: null }) },
  // v1.1.0: #/projects → 兼容重定向到关卡列表
  { pattern: /^#?\/projects$/, map: () => ({ screen: "levels", projectId: null }) },
  { pattern: /^#?\/settings$/, map: () => ({ screen: "settings", projectId: null }) },
  {
    pattern: /^#?\/editor\/([^/]+)$/,
    map: (m) => ({ screen: "editor", projectId: m[1] }),
  },
  {
    pattern: /^#?\/battle\/([^/]+)$/,
    map: (m) => ({ screen: "battle", projectId: m[1] }),
  },
  {
    pattern: /^#?\/result\/([^/]+)$/,
    map: (m) => ({ screen: "result", projectId: m[1] }),
  },
  // V2 屏 7: 地图编辑器（Phase 4 实现）
  {
    pattern: /^#?\/editor-map\/([^/]+)$/,
    map: (m) => ({ screen: "editor-map", projectId: m[1] }),
  },
  {
    pattern: /^#?\/editor-map$/,
    map: () => ({ screen: "editor-map", projectId: "new" }),
  },
  // 屏 8: Evolution Arena
  { pattern: /^#?\/arena$/, map: () => ({ screen: "arena", projectId: null }) },
  // 屏 9: Showcase Validator
  { pattern: /^#?\/showcase-validator$/, map: () => ({ screen: "showcase-validator", projectId: null }) },
  // V6.1 屏 10: 砺锋台 V5 唯一入口
  { pattern: /^#?\/sharpen$/, map: () => ({ screen: "sharpen", projectId: null }) },
  // V8 屏 11: 砺锋台中转页（剑从鞘中抽出 + 铸剑行绝句，强制中转不可跳过）
  { pattern: /^#?\/sharpen-transition$/, map: () => ({ screen: "sharpen-transition", projectId: null }) },
  // V8: #/forge → 屏 12 铸剑阁（学习体系入口）
  { pattern: /^#?\/forge$/, map: () => ({ screen: "forge", projectId: null }) },
  // V9: #/my-works → 屏 13 我的作品集（作品选择器 + 铸剑阁成就展示）
  { pattern: /^#?\/my-works$/, map: () => ({ screen: "my-works", projectId: null }) },
  // V11: #/armory-hall → 屏 14 兵主殿（导航枢纽，2026-07-24）
  { pattern: /^#?\/armory-hall$/, map: () => ({ screen: "armory-hall", projectId: null }) },
  // V11: #/transition → 屏 15 通用过渡动画（参数化，2026-07-24）
  { pattern: /^#?\/transition$/, map: () => ({ screen: "transition", projectId: null }) },
  // V11: #/auth-callback → 微信登录回调（JWT 解析 + 重定向）
  { pattern: /^#?\/auth-callback/, map: () => ({ screen: "auth-callback", projectId: null }) },
  // V13: #/snapshot-collector → 屏 16 Test Harness 快照采集
  { pattern: /^#?\/snapshot-collector$/, map: () => ({ screen: "snapshot-collector", projectId: null }) },
];

/**
 * v6.1.0: 旧路由 deprecation 检测 + 自动跳转
 * 返回 true 表示命中旧路由（调用方应避免重复处理）
 */
export function handleDeprecatedHash(hash: string): boolean {
  const normalized = hash.startsWith("#") ? hash : `#${hash}`;
  const note = DEPRECATED_SHARPEN_HASHES[normalized];
  if (note) {
    console.warn(`[router] ${note}/sharpen → 自动跳转 #/sharpen`);
    window.location.hash = "#/sharpen";
    return true;
  }
  return false;
}

export function parseHash(hash: string = window.location.hash): RouteState {
  if (handleDeprecatedHash(hash)) {
    // 命中旧路由，setter 触发 hashchange → handleRoute 重新跑
    return { screen: "home", projectId: null };
  }
  for (const r of ROUTES) {
    const m = hash.match(r.pattern);
    if (m) return r.map(m);
  }
  return { screen: "home", projectId: null };
}

export type RouteHandler = (state: RouteState) => void;

export class ScreenRouter {
  private handler: RouteHandler | null = null;
  private currentState: RouteState = { screen: "home", projectId: null };

  start() {
    window.addEventListener("hashchange", () => this.handleRoute());
    this.handleRoute();
  }

  private handleRoute() {
    this.currentState = parseHash();
    if (this.handler) this.handler(this.currentState);
  }

  onChange(handler: RouteHandler) {
    this.handler = handler;
  }

  go(path: string) {
    if (window.location.hash === path) {
      // 强制触发（hash 未变）
      this.handleRoute();
    } else {
      window.location.hash = path;
    }
  }

  getState(): RouteState {
    return this.currentState;
  }
}

export function pathFor(screen: ScreenId, projectId?: string | null): string {
  switch (screen) {
    case "home":
      return "#/";
    case "levels":
    case "projects":
      return "#/levels";
    case "settings":
      return "#/settings";
    case "editor":
      return `#/editor/${projectId}`;
    case "battle":
      return `#/battle/${projectId}`;
    case "result":
      return `#/result/${projectId}`;
    case "editor-map":
      return `#/editor-map/${projectId}`;
    case "arena":
      return "#/arena";
    case "showcase-validator":
      return "#/showcase-validator";
    case "sharpen":
      return "#/sharpen";
    case "sharpen-transition":
      return "#/sharpen-transition";
    case "forge":
      return "#/forge";
    case "my-works":
      return "#/my-works";
    case "armory-hall":
      return "#/armory-hall";
    case "transition":
      return "#/transition";
    case "auth-callback":
      return "#/auth-callback";
    case "snapshot-collector":
      return "#/snapshot-collector";
  }
}
